import _ from "lodash";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useI18n } from "Hooks";

// components
import Loading from "UIComponents/Loading";
import LessonPlanningAssistant from "ClassRoom/components/AddAssignment/LessonPlanningAssistant";
import AITutorChatWrapper from "AppComponents/AITutorChatWrapper";

// design system
import { SegmentControl, Tooltip, IconButton } from "@toddle-edu/ds-web";
import { RefreshArrowOutlined } from "@toddle-edu/ds-icons";
import { colors } from "@toddle-edu/ds-theme";

// third party
import { connect } from "react-redux";

// modules
import { ResourceDetailsContext } from "UnitPlans/modules/UnitPlanModule";
import {
  updateAIDataInRedux,
  updateAssessmentInRedux,
} from "Assessments/modules/AssessmentModule";

// constants
import {
  AI_TUTOR_TAB_OPTIONS,
  TASK_DETAILS,
  CHAT,
} from "../../modules/Constants";

// scss
import classes from "./AITutor.scss";

const styles = {
  loadingContainerStyle: {
    position: "absolute",
    backgroundColor: colors.surfaceWhite,
    width: "100%",
    height: "100%",
    inset: 0,
    zIndex: 100000,
  },
};

const testId = "assessment-aiTutor";

const AITutor = props => {
  const {
    portalType = "PLANNER",
    sourceFrom,
    resourceId,
    assessmentMode,
    mode,
    courseId,
    selectedCourseId,
    selectedCourse,
    unitPlanId,
    children,
    middlePaneRef,
    selectedTab,
    updateAIDataInRedux,
    learningCourseId,
  } = props;

  const { t } = useI18n(["common", "classRoom"]);

  const [isLoading, setIsLoading] = useState(false);
  const [resetIconInfo, setResetIconInfo] = useState({});

  const isRefSet = useRef(false);
  const customRef = useRef(null);

  // this will make sure that middlePaneRef is set before we show the component
  useEffect(() => {
    if (isRefSet.current) {
      setIsLoading(false);
      return;
    }

    if (!middlePaneRef) {
      setIsLoading(true);
      setTimeout(() => updateSelectedTab(TASK_DETAILS));
    } else {
      setTimeout(() => {
        isRefSet.current = true;
        updateSelectedTab(CHAT);
        setIsLoading(false);
      }, 1000);
    }
  }, [middlePaneRef, resetIconInfo, isLoading]);

  // in the CHAT tab a reset chat icon button is there on the right of SegmentControl
  // when user switches to TASK_DETAILS tab then we need to hide the button
  useEffect(() => {
    if (selectedTab !== CHAT) setResetIconInfo({});
  }, [selectedTab]);

  const customCallbackRef = useCallback(
    node => {
      if (node) {
        const { messages, isAIResponseLoading, setCurrentDialogueAction } =
          node || {};

        customRef.current = { setCurrentDialogueAction };

        const showIcon = selectedTab === CHAT && !_.isEmpty(messages);

        setResetIconInfo(prevState => ({
          ...prevState,
          disableIcon: isAIResponseLoading,
          showIcon,
        }));
      }
    },
    [selectedTab]
  );

  const updateSelectedTab = tab => {
    updateAIDataInRedux({
      aiTutorSelectedTab: tab,
    });
  };

  const renderLeftUI = assessmentDetails => {
    const { isDraft, isFromUnitPlan, creationMode } = assessmentDetails || {};

    switch (selectedTab) {
      case CHAT:
        return (
          <LessonPlanningAssistant
            portalType={portalType}
            sourceFrom={sourceFrom}
            leId={resourceId}
            mode={assessmentMode || mode}
            courseId={courseId || selectedCourseId}
            selectedCourse={selectedCourse}
            unitPlanId={unitPlanId}
            isFromUnitPlan={isFromUnitPlan}
            creationMode={creationMode}
            isDraft={isDraft}
            isAITutor={true}
            middlePaneRef={middlePaneRef}
            customRef={customCallbackRef}
            testId={testId}
            learningCourseId={learningCourseId}
          />
        );
      case TASK_DETAILS:
        return <div className={classes.overflowYAuto}>{children}</div>;
      default:
        return null;
    }
  };

  const renderRightUI = () => {
    return (
      <AITutorChatWrapper
        leId={resourceId}
        assessmentMode={assessmentMode || mode}
        entityId={resourceId}
        source={"PREVIEW_CHAT"}
        learningCourseId={learningCourseId}
      />
    );
  };

  const onClickResetChat = () => {
    customRef?.current?.setCurrentDialogueAction?.({ action: "CLEAR_CHAT" });
  };

  const { showIcon, disableIcon } = resetIconInfo;

  const options = _.map(AI_TUTOR_TAB_OPTIONS, option => ({
    ...option,
    label: t(option.label),
  }));

  return (
    <ResourceDetailsContext.Consumer>
      {assessmentDetails => {
        return (
          <div className={classes.container}>
            <div className={classes.leftContainer}>
              <div className={classes.segmentControlContainer}>
                <div className={classes.segmentControl}>
                  <SegmentControl
                    dsVersion={"2.0"}
                    size={"medium"}
                    options={options}
                    value={selectedTab}
                    onChange={updateSelectedTab}
                    testId={`${testId}-segmentControl`}
                  />
                </div>

                {showIcon ? (
                  <div className={classes.clearChat}>
                    <Tooltip
                      tooltip={t("common:clear_chat_history")}
                      placement={"bottom"}
                      dsVersion={"2.0"}
                      showArrow={true}
                    >
                      <IconButton
                        dsVersion={"2.0"}
                        size={"medium"}
                        variant={"neutral"}
                        type={"plain"}
                        disabled={disableIcon}
                        icon={<RefreshArrowOutlined />}
                        onClick={onClickResetChat}
                        testId={`${testId}-resetChat`}
                      />
                    </Tooltip>
                  </div>
                ) : null}
              </div>

              {renderLeftUI(assessmentDetails)}

              {isLoading ? (
                <Loading containerStyle={styles.loadingContainerStyle} />
              ) : null}
            </div>

            <div className={classes.rightContainer}>{renderRightUI()}</div>
          </div>
        );
      }}
    </ResourceDetailsContext.Consumer>
  );
};

const mapStateToProps = state => {
  const aiData = _.get(state, "assessment.aiData", {});
  const selectedTab = _.get(aiData, "aiTutorSelectedTab");

  return { selectedTab: selectedTab || CHAT };
};

const mapActionCreators = {
  updateAIDataInRedux,
  updateAssessmentInRedux,
};

export default connect(mapStateToProps, mapActionCreators)(AITutor);
